/**
 * Check-In/Check-Out Agent - Reservation Report
 * View complete reservation report (same as Super Admin)
 */

import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import Alert from "@mui/material/Alert";
import Divider from "@mui/material/Divider";
import CircularProgress from "@mui/material/CircularProgress";
import Icon from "@mui/material/Icon";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import apiRequest from "utils/api";

function CheckInOutReservationDetails() {
  const navigate = useNavigate();
  const { id } = useParams();

  const [loading, setLoading] = useState(true);
  const [reservation, setReservation] = useState(null);
  const [message, setMessage] = useState({ type: "", text: "" });

  useEffect(() => {
    const fetchReservation = async () => {
      try {
        setLoading(true);
        setMessage({ type: "", text: "" });
        const res = await apiRequest(`/reservations/${id}`);
        const reservationData = res.reservation || res.data?.reservation || res.data;
        setReservation(reservationData);
      } catch (error) {
        console.error("Error fetching reservation details:", error);
        setMessage({ type: "error", text: error.message || "Failed to load reservation report" });
      } finally {
        setLoading(false);
      }
    };
    if (id) fetchReservation();
  }, [id]);

  const currency = reservation?.pricing?.currency || "EGP";
  const formatCurrency = (amount) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
      minimumFractionDigits: 0,
    }).format(amount || 0);

  const formatDateTime = (date) =>
    date
      ? new Date(date).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" })
      : "—";

  const getScheduledCheckInTime = () => {
    if (reservation?.requestedCheckInTime) {
      return new Date(reservation.requestedCheckInTime).toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
    }
    const earlyHours = reservation?.earlyCheckInHours ?? 0;
    const hour = Math.max(0, Math.min(14, 14 - earlyHours));
    return new Date(2000, 0, 1, hour, 0, 0).toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  const getScheduledCheckOutTime = () => {
    if (
      reservation?.earlyCheckout?.type === "sameDay" &&
      reservation?.earlyCheckout?.status === "completed" &&
      reservation?.earlyCheckout?.requestedCheckOutTime
    ) {
      return new Date(reservation.earlyCheckout.requestedCheckOutTime).toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
    }
    if (reservation?.lateCheckOutHours > 0 && reservation?.requestedCheckOutTime) {
      return new Date(reservation.requestedCheckOutTime).toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
    }
    return "12:00 PM";
  };

  const totalAmount = reservation?.pricing?.totalAmount || 0;
  const profitPercentage = reservation?.property?.profitPercentage || 0;
  const platformFee = (totalAmount * profitPercentage) / 100;
  const insuranceDeposit =
    reservation?.pricing?.insurancePrice || reservation?.property?.insurancePrice || 0;

  const statusChip = useMemo(() => {
    const s = reservation?.status || "Confirmed";
    let label = s.replace(/_/g, " ").toUpperCase();
    let color = "#1976d2";
    if (s === "CheckedIn") {
      label = "CHECKED IN";
      color = "#4caf50";
    } else if (s === "CheckedOut") {
      label = "CHECKED OUT";
      color = "#9e9e9e";
    } else if (s === "Cancelled" || s === "Refunded") color = "#f44336";
    return { label, color };
  }, [reservation]);

  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox py={3} display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </MDBox>
        <Footer />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <MDBox
          mb={3}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          gap={2}
        >
          <MDButton
            variant="outlined"
            color="secondary"
            onClick={() => navigate("/checkin-out")}
            startIcon={<Icon>arrow_back</Icon>}
          >
            Back to Check-In/Out
          </MDButton>
          {reservation?.reservationNumber && (
            <Chip label={reservation.reservationNumber} size="small" sx={{ fontWeight: 700 }} />
          )}
        </MDBox>

        {message.text && (
          <Alert severity={message.type === "error" ? "error" : "success"} sx={{ mb: 3 }}>
            {message.text}
          </Alert>
        )}

        {!reservation ? (
          <Card sx={{ p: 4, borderRadius: 3 }}>
            <MDTypography variant="h6" fontWeight="bold">
              Reservation not found
            </MDTypography>
          </Card>
        ) : (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Card sx={{ p: 3, borderRadius: 3 }}>
                <MDBox
                  display="flex"
                  justifyContent="space-between"
                  alignItems="flex-start"
                  flexWrap="wrap"
                  gap={2}
                >
                  <MDBox>
                    <MDTypography variant="h5" fontWeight="bold" mb={0.5}>
                      {reservation.property?.title?.en || reservation.property?.title || "N/A"}
                    </MDTypography>
                    {reservation.property?.shortId && (
                      <MDTypography variant="body2" color="text.secondary">
                        ID: {reservation.property.shortId}
                      </MDTypography>
                    )}
                  </MDBox>
                  <Chip
                    label={statusChip.label}
                    sx={{ bgcolor: statusChip.color, color: "white", fontWeight: 700 }}
                    size="small"
                  />
                </MDBox>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card sx={{ p: 3, borderRadius: 3, height: "100%" }}>
                <MDTypography
                  variant="h6"
                  fontWeight="bold"
                  mb={2}
                  display="flex"
                  alignItems="center"
                  gap={1}
                >
                  <Icon color="info">event</Icon> Booking Details
                </MDTypography>
                <Divider sx={{ mb: 2 }} />
                <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <MDTypography variant="body2" color="text.secondary">
                    Check-in date
                  </MDTypography>
                  <MDTypography variant="body2" fontWeight="medium">
                    {new Date(reservation.checkInDate).toLocaleDateString()}
                  </MDTypography>
                </MDBox>
                <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <MDTypography variant="body2" color="text.secondary">
                    Check-in time (scheduled)
                  </MDTypography>
                  <MDTypography variant="body2" fontWeight="medium">
                    {getScheduledCheckInTime()}
                  </MDTypography>
                </MDBox>
                <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <MDTypography variant="body2" color="text.secondary">
                    Actual check-in time
                  </MDTypography>
                  <MDTypography variant="body2" fontWeight="medium">
                    {reservation.checkIn?.checkedInAt
                      ? formatDateTime(reservation.checkIn.checkedInAt)
                      : "—"}
                  </MDTypography>
                </MDBox>
                <Divider sx={{ my: 2 }} />
                <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <MDTypography variant="body2" color="text.secondary">
                    Check-out date
                  </MDTypography>
                  <MDTypography variant="body2" fontWeight="medium">
                    {new Date(reservation.checkOutDate).toLocaleDateString()}
                  </MDTypography>
                </MDBox>
                <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <MDTypography variant="body2" color="text.secondary">
                    Check-out time (scheduled)
                  </MDTypography>
                  <MDTypography variant="body2" fontWeight="medium">
                    {getScheduledCheckOutTime()}
                  </MDTypography>
                </MDBox>
                <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <MDTypography variant="body2" color="text.secondary">
                    Actual check-out time
                  </MDTypography>
                  <MDTypography variant="body2" fontWeight="medium">
                    {reservation.checkOut?.checkedOutAt
                      ? formatDateTime(reservation.checkOut.checkedOutAt)
                      : "—"}
                  </MDTypography>
                </MDBox>
                <Divider sx={{ my: 2 }} />
                <MDBox display="flex" justifyContent="space-between" alignItems="center">
                  <MDTypography variant="body2" color="text.secondary">
                    Guests
                  </MDTypography>
                  <MDTypography variant="body2" fontWeight="medium">
                    {reservation.numberOfGuests || 0}
                  </MDTypography>
                </MDBox>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card sx={{ p: 3, borderRadius: 3, height: "100%" }}>
                <MDTypography
                  variant="h6"
                  fontWeight="bold"
                  mb={2}
                  display="flex"
                  alignItems="center"
                  gap={1}
                >
                  <Icon color="success">paid</Icon> Finance Details
                </MDTypography>
                <Divider sx={{ mb: 2 }} />

                {/* Base Pricing */}
                <MDTypography
                  variant="caption"
                  fontWeight="bold"
                  color="text.secondary"
                  mb={1}
                  display="block"
                >
                  Base Pricing
                </MDTypography>
                <MDBox display="flex" justifyContent="space-between" mb={0.5}>
                  <MDTypography variant="body2" color="text.secondary">
                    {reservation.pricing?.numberOfNights || 0} night(s) ×{" "}
                    {formatCurrency(reservation.pricing?.pricePerNight || 0)}
                  </MDTypography>
                  <MDTypography variant="body2" fontWeight="medium">
                    {formatCurrency(reservation.pricing?.subtotal || 0)}
                  </MDTypography>
                </MDBox>

                {/* Add-on Fees */}
                {(reservation.pricing?.earlyCheckInFee > 0 ||
                  reservation.pricing?.lateCheckOutFee > 0 ||
                  reservation.earlyCheckout?.type === "byDays") && (
                  <>
                    <Divider sx={{ my: 1.5 }} />
                    <MDTypography
                      variant="caption"
                      fontWeight="bold"
                      color="text.secondary"
                      mb={1}
                      display="block"
                    >
                      Add-on Fees
                    </MDTypography>
                    {reservation.pricing?.earlyCheckInFee > 0 && (
                      <MDBox display="flex" justifyContent="space-between" mb={0.5}>
                        <MDTypography variant="body2" color="text.secondary">
                          Early Check-in Fee
                        </MDTypography>
                        <MDTypography variant="body2" fontWeight="medium" color="info.main">
                          +{formatCurrency(reservation.pricing.earlyCheckInFee)}
                        </MDTypography>
                      </MDBox>
                    )}
                    {reservation.pricing?.lateCheckOutFee > 0 && (
                      <MDBox display="flex" justifyContent="space-between" mb={0.5}>
                        <MDTypography variant="body2" color="text.secondary">
                          Late Checkout Fee
                        </MDTypography>
                        <MDTypography variant="body2" fontWeight="medium" color="info.main">
                          +{formatCurrency(reservation.pricing.lateCheckOutFee)}
                        </MDTypography>
                      </MDBox>
                    )}
                    {reservation.earlyCheckout?.type === "byDays" &&
                      reservation.earlyCheckout?.status === "completed" && (
                        <>
                          <MDBox display="flex" justifyContent="space-between" mb={0.5}>
                            <MDTypography variant="body2" color="text.secondary">
                              Early Checkout Fee
                            </MDTypography>
                            <MDTypography variant="body2" fontWeight="medium" color="error.main">
                              +{formatCurrency(reservation.earlyCheckout.feeAmount || 0)}
                            </MDTypography>
                          </MDBox>
                          <MDBox display="flex" justifyContent="space-between" mb={0.5}>
                            <MDTypography variant="body2" color="text.secondary">
                              Early Checkout Refund
                            </MDTypography>
                            <MDTypography variant="body2" fontWeight="medium" color="success.main">
                              -{formatCurrency(reservation.earlyCheckout.refundAmount || 0)}
                            </MDTypography>
                          </MDBox>
                        </>
                      )}
                  </>
                )}

                {/* Discounts */}
                {reservation.pricing?.discount > 0 && (
                  <>
                    <Divider sx={{ my: 1.5 }} />
                    <MDBox display="flex" justifyContent="space-between" mb={0.5}>
                      <MDTypography variant="body2" color="text.secondary">
                        Discount
                      </MDTypography>
                      <MDTypography variant="body2" fontWeight="medium" color="success.main">
                        -{formatCurrency(reservation.pricing.discount)}
                      </MDTypography>
                    </MDBox>
                  </>
                )}

                {/* Insurance */}
                <Divider sx={{ my: 1.5 }} />
                <MDBox display="flex" justifyContent="space-between" mb={0.5}>
                  <MDTypography variant="body2" color="text.secondary">
                    Insurance Deposit
                  </MDTypography>
                  <MDTypography variant="body2" fontWeight="medium">
                    {formatCurrency(insuranceDeposit)}
                  </MDTypography>
                </MDBox>

                {/* Total */}
                <Divider sx={{ my: 1.5 }} />
                <MDBox
                  display="flex"
                  justifyContent="space-between"
                  mb={1}
                  sx={{ bgcolor: "success.lighter", p: 1, borderRadius: 1 }}
                >
                  <MDTypography variant="body2" fontWeight="bold">
                    Total Paid by Client
                  </MDTypography>
                  <MDTypography variant="body2" fontWeight="bold" color="success.main">
                    {formatCurrency(totalAmount)}
                  </MDTypography>
                </MDBox>

                {/* Platform Revenue */}
                <Divider sx={{ my: 1.5 }} />
                <MDTypography
                  variant="caption"
                  fontWeight="bold"
                  color="text.secondary"
                  mb={1}
                  display="block"
                >
                  Revenue Split
                </MDTypography>
                <MDBox display="flex" justifyContent="space-between" mb={0.5}>
                  <MDTypography variant="body2" color="text.secondary">
                    Platform Fee ({profitPercentage}%)
                  </MDTypography>
                  <MDTypography variant="body2" fontWeight="bold" color="warning.main">
                    {formatCurrency(platformFee)}
                  </MDTypography>
                </MDBox>
                <MDBox display="flex" justifyContent="space-between">
                  <MDTypography variant="body2" color="text.secondary">
                    Property Owner Revenue
                  </MDTypography>
                  <MDTypography variant="body2" fontWeight="bold" color="info.main">
                    {formatCurrency(totalAmount - platformFee)}
                  </MDTypography>
                </MDBox>
              </Card>
            </Grid>

            {/* Reservation Modifications & Add-ons */}
            <Grid item xs={12}>
              <Card
                sx={{ p: 3, borderRadius: 3, border: "1px solid", borderColor: "warning.light" }}
              >
                <MDTypography
                  variant="h6"
                  fontWeight="bold"
                  mb={2}
                  display="flex"
                  alignItems="center"
                  gap={1}
                >
                  <Icon color="warning">edit_calendar</Icon> Reservation Modifications & Add-ons
                </MDTypography>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={3}>
                  {/* Early Check-in */}
                  <Grid item xs={12} md={6} lg={4}>
                    <MDBox
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        bgcolor:
                          reservation.requestedCheckInTime || reservation.earlyCheckInHours > 0
                            ? "success.lighter"
                            : "grey.100",
                      }}
                    >
                      <MDTypography
                        variant="subtitle2"
                        fontWeight="bold"
                        mb={1}
                        display="flex"
                        alignItems="center"
                        gap={0.5}
                      >
                        <Icon
                          fontSize="small"
                          color={
                            reservation.requestedCheckInTime || reservation.earlyCheckInHours > 0
                              ? "success"
                              : "disabled"
                          }
                        >
                          login
                        </Icon>
                        Early Check-in
                      </MDTypography>
                      {reservation.requestedCheckInTime || reservation.earlyCheckInHours > 0 ? (
                        <>
                          <MDTypography variant="body2" color="success.main" fontWeight="medium">
                            {getScheduledCheckInTime()} (instead of 2:00 PM)
                          </MDTypography>
                          {reservation.pricing?.earlyCheckInFee > 0 && (
                            <MDTypography variant="caption" color="text.secondary">
                              Fee: {formatCurrency(reservation.pricing.earlyCheckInFee)}
                            </MDTypography>
                          )}
                        </>
                      ) : (
                        <MDTypography variant="body2" color="text.secondary">
                          Not requested
                        </MDTypography>
                      )}
                    </MDBox>
                  </Grid>

                  {/* Early Checkout - Same Day */}
                  <Grid item xs={12} md={6} lg={4}>
                    <MDBox
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        bgcolor:
                          reservation.earlyCheckout?.type === "sameDay"
                            ? "info.lighter"
                            : "grey.100",
                      }}
                    >
                      <MDTypography
                        variant="subtitle2"
                        fontWeight="bold"
                        mb={1}
                        display="flex"
                        alignItems="center"
                        gap={0.5}
                      >
                        <Icon
                          fontSize="small"
                          color={
                            reservation.earlyCheckout?.type === "sameDay" ? "info" : "disabled"
                          }
                        >
                          schedule
                        </Icon>
                        Early Checkout (Same Day)
                      </MDTypography>
                      {reservation.earlyCheckout?.type === "sameDay" &&
                      reservation.earlyCheckout?.status === "completed" ? (
                        <>
                          <MDTypography variant="body2" color="info.main" fontWeight="medium">
                            {getScheduledCheckOutTime()} (instead of 12:00 PM)
                          </MDTypography>
                          <MDTypography variant="caption" color="text.secondary">
                            No fee applied
                          </MDTypography>
                        </>
                      ) : (
                        <MDTypography variant="body2" color="text.secondary">
                          Not requested
                        </MDTypography>
                      )}
                    </MDBox>
                  </Grid>

                  {/* Early Checkout - By Days */}
                  <Grid item xs={12} md={6} lg={4}>
                    <MDBox
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        bgcolor:
                          reservation.earlyCheckout?.type === "byDays"
                            ? "warning.lighter"
                            : "grey.100",
                      }}
                    >
                      <MDTypography
                        variant="subtitle2"
                        fontWeight="bold"
                        mb={1}
                        display="flex"
                        alignItems="center"
                        gap={0.5}
                      >
                        <Icon
                          fontSize="small"
                          color={
                            reservation.earlyCheckout?.type === "byDays" ? "warning" : "disabled"
                          }
                        >
                          event_busy
                        </Icon>
                        Early Checkout (By Days)
                      </MDTypography>
                      {reservation.earlyCheckout?.type === "byDays" &&
                      reservation.earlyCheckout?.status === "completed" ? (
                        <>
                          <MDTypography variant="body2" color="warning.main" fontWeight="medium">
                            Original:{" "}
                            {reservation.earlyCheckout.originalCheckOutDate
                              ? new Date(
                                  reservation.earlyCheckout.originalCheckOutDate
                                ).toLocaleDateString()
                              : "—"}
                          </MDTypography>
                          <MDTypography variant="body2" color="warning.main" fontWeight="medium">
                            New:{" "}
                            {reservation.earlyCheckout.requestedCheckOutDate
                              ? new Date(
                                  reservation.earlyCheckout.requestedCheckOutDate
                                ).toLocaleDateString()
                              : new Date(reservation.checkOutDate).toLocaleDateString()}
                          </MDTypography>
                          <MDBox display="flex" gap={2} mt={0.5}>
                            <MDTypography variant="caption" color="error.main">
                              Fee: {formatCurrency(reservation.earlyCheckout.feeAmount || 0)}
                            </MDTypography>
                            <MDTypography variant="caption" color="success.main">
                              Refund: {formatCurrency(reservation.earlyCheckout.refundAmount || 0)}
                            </MDTypography>
                          </MDBox>
                        </>
                      ) : (
                        <MDTypography variant="body2" color="text.secondary">
                          Not requested
                        </MDTypography>
                      )}
                    </MDBox>
                  </Grid>

                  {/* Late Checkout */}
                  <Grid item xs={12} md={6} lg={4}>
                    <MDBox
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        bgcolor:
                          reservation.lateCheckOutHours > 0 ? "secondary.lighter" : "grey.100",
                      }}
                    >
                      <MDTypography
                        variant="subtitle2"
                        fontWeight="bold"
                        mb={1}
                        display="flex"
                        alignItems="center"
                        gap={0.5}
                      >
                        <Icon
                          fontSize="small"
                          color={reservation.lateCheckOutHours > 0 ? "secondary" : "disabled"}
                        >
                          logout
                        </Icon>
                        Late Checkout
                      </MDTypography>
                      {reservation.lateCheckOutHours > 0 ? (
                        <>
                          <MDTypography variant="body2" color="secondary.main" fontWeight="medium">
                            {reservation.requestedCheckOutTime
                              ? new Date(reservation.requestedCheckOutTime).toLocaleTimeString(
                                  "en-US",
                                  { hour: "numeric", minute: "2-digit", hour12: true }
                                )
                              : `${reservation.lateCheckOutHours} hour(s) after 12:00 PM`}
                          </MDTypography>
                          {reservation.pricing?.lateCheckOutFee > 0 && (
                            <MDTypography variant="caption" color="text.secondary">
                              Fee: {formatCurrency(reservation.pricing.lateCheckOutFee)}
                            </MDTypography>
                          )}
                        </>
                      ) : (
                        <MDTypography variant="body2" color="text.secondary">
                          Not requested
                        </MDTypography>
                      )}
                    </MDBox>
                  </Grid>

                  {/* Extended Stay */}
                  <Grid item xs={12} md={6} lg={4}>
                    <MDBox
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        bgcolor: reservation.extendedStayRequestedAt
                          ? "primary.lighter"
                          : "grey.100",
                      }}
                    >
                      <MDTypography
                        variant="subtitle2"
                        fontWeight="bold"
                        mb={1}
                        display="flex"
                        alignItems="center"
                        gap={0.5}
                      >
                        <Icon
                          fontSize="small"
                          color={reservation.extendedStayRequestedAt ? "primary" : "disabled"}
                        >
                          add_circle
                        </Icon>
                        Extended Stay
                      </MDTypography>
                      {reservation.extendedStayRequestedAt ? (
                        <>
                          <MDTypography variant="body2" color="primary.main" fontWeight="medium">
                            Stay extended
                          </MDTypography>
                          {reservation.originalCheckOutDateBeforeExtension && (
                            <MDTypography variant="caption" color="text.secondary">
                              Original checkout:{" "}
                              {new Date(
                                reservation.originalCheckOutDateBeforeExtension
                              ).toLocaleDateString()}
                            </MDTypography>
                          )}
                        </>
                      ) : (
                        <MDTypography variant="body2" color="text.secondary">
                          Not requested
                        </MDTypography>
                      )}
                    </MDBox>
                  </Grid>

                  {/* Booking Source */}
                  <Grid item xs={12} md={6} lg={4}>
                    <MDBox sx={{ p: 2, borderRadius: 2, bgcolor: "grey.100" }}>
                      <MDTypography
                        variant="subtitle2"
                        fontWeight="bold"
                        mb={1}
                        display="flex"
                        alignItems="center"
                        gap={0.5}
                      >
                        <Icon fontSize="small">language</Icon>
                        Booking Source
                      </MDTypography>
                      <MDTypography variant="body2" fontWeight="medium">
                        {reservation.bookingSource || "Website"}
                      </MDTypography>
                    </MDBox>
                  </Grid>
                </Grid>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <Card sx={{ p: 3, borderRadius: 3, border: "1px solid", borderColor: "info.light" }}>
                <MDTypography
                  variant="h6"
                  fontWeight="bold"
                  mb={2}
                  display="flex"
                  alignItems="center"
                  gap={1}
                >
                  <Icon color="info">notes</Icon> Inspector Note & Special Requests
                </MDTypography>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <MDBox>
                      <MDTypography
                        variant="subtitle2"
                        fontWeight="bold"
                        mb={1}
                        color="text.secondary"
                      >
                        Inspector Note
                      </MDTypography>
                      <MDTypography variant="body2">
                        {reservation.checkIn?.notes || "No inspector note recorded."}
                      </MDTypography>
                    </MDBox>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <MDBox>
                      <MDTypography
                        variant="subtitle2"
                        fontWeight="bold"
                        mb={1}
                        color="text.secondary"
                      >
                        Special Requests
                      </MDTypography>
                      <MDTypography variant="body2">
                        {reservation.specialRequests || "No special requests from guest."}
                      </MDTypography>
                    </MDBox>
                  </Grid>
                </Grid>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <Card sx={{ p: 3, borderRadius: 3 }}>
                <MDTypography
                  variant="h6"
                  fontWeight="bold"
                  mb={2}
                  display="flex"
                  alignItems="center"
                  gap={1}
                >
                  <Icon color="info">badge</Icon> Guest Names & Photo IDs
                </MDTypography>
                <Divider sx={{ mb: 2 }} />
                {(() => {
                  const guests = reservation.checkIn?.guests;
                  const hasGuests = Array.isArray(guests) && guests.length > 0;
                  const clientName =
                    reservation.client?.fullName ||
                    [reservation.client?.firstName, reservation.client?.lastName]
                      .filter(Boolean)
                      .join(" ") ||
                    reservation.client?.email;
                  const fallbackGuests =
                    !hasGuests &&
                    (reservation.status === "CheckedIn" || reservation.status === "CheckedOut") &&
                    clientName
                      ? [{ name: clientName, idFrontPhoto: null, idBackPhoto: null }]
                      : [];
                  const displayGuests = hasGuests ? guests : fallbackGuests;
                  return displayGuests.length > 0 ? (
                    <Grid container spacing={3}>
                      {displayGuests.map((guest, index) => (
                        <Grid item xs={12} md={6} lg={4} key={index}>
                          <Card
                            sx={{
                              p: 2.5,
                              borderRadius: 2,
                              boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
                            }}
                          >
                            <MDTypography
                              variant="subtitle1"
                              fontWeight="bold"
                              mb={1.5}
                              color="info.main"
                            >
                              Guest {index + 1}: {guest.name || "N/A"}
                            </MDTypography>
                            {(guest.idFrontPhoto || guest.idBackPhoto) && (
                              <MDBox
                                sx={{ display: "flex", flexDirection: "row", gap: 2, mt: 1.5 }}
                              >
                                {guest.idFrontPhoto && (
                                  <MDBox
                                    component="img"
                                    src={guest.idFrontPhoto}
                                    alt="ID Front"
                                    sx={{
                                      width: 100,
                                      height: 70,
                                      objectFit: "cover",
                                      borderRadius: 1,
                                      cursor: "pointer",
                                    }}
                                    onClick={() => window.open(guest.idFrontPhoto, "_blank")}
                                  />
                                )}
                                {guest.idBackPhoto && (
                                  <MDBox
                                    component="img"
                                    src={guest.idBackPhoto}
                                    alt="ID Back"
                                    sx={{
                                      width: 100,
                                      height: 70,
                                      objectFit: "cover",
                                      borderRadius: 1,
                                      cursor: "pointer",
                                    }}
                                    onClick={() => window.open(guest.idBackPhoto, "_blank")}
                                  />
                                )}
                              </MDBox>
                            )}
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  ) : (
                    <MDTypography variant="body2" color="text.secondary">
                      No guest details yet.
                    </MDTypography>
                  );
                })()}
              </Card>
            </Grid>

            <Grid item xs={12}>
              <Card sx={{ p: 3, borderRadius: 3, border: "1px solid", borderColor: "grey.200" }}>
                <MDTypography
                  variant="h6"
                  fontWeight="bold"
                  mb={2}
                  display="flex"
                  alignItems="center"
                  gap={1}
                >
                  <Icon color="info">checklist</Icon> Criteria Report (Check-in & Check-out)
                </MDTypography>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <MDBox>
                      <MDTypography
                        variant="subtitle2"
                        fontWeight="bold"
                        mb={1}
                        color="success.main"
                      >
                        Confirmed at Check-In
                      </MDTypography>
                      {reservation.checkIn?.criteriaConfirmed?.length > 0 ? (
                        <MDBox component="ul" sx={{ m: 0, pl: 2 }}>
                          {reservation.checkIn.criteriaConfirmed
                            .filter(
                              (c) =>
                                c.present !== false &&
                                (c.quantifiable ? (c.quantity ?? 0) > 0 : true)
                            )
                            .map((c, i) => (
                              <li key={i}>
                                <MDTypography variant="body2">
                                  {c.criterionName || c.criterionNameAr || "Item"}
                                  {c.quantifiable && c.quantity != null && ` (${c.quantity} pcs)`}
                                </MDTypography>
                              </li>
                            ))}
                        </MDBox>
                      ) : (
                        <MDTypography variant="body2" color="text.secondary">
                          No criteria recorded at check-in.
                        </MDTypography>
                      )}
                    </MDBox>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <MDBox>
                      <MDTypography variant="subtitle2" fontWeight="bold" mb={1} color="info.main">
                        Confirmed at Check-Out
                      </MDTypography>
                      {reservation.checkOut?.criteriaConfirmed?.length > 0 ? (
                        <MDBox component="ul" sx={{ m: 0, pl: 2 }}>
                          {reservation.checkOut.criteriaConfirmed.map((c, i) => (
                            <li key={i}>
                              <MDTypography
                                variant="body2"
                                color={
                                  c.present === false || (c.quantifiable && (c.quantity ?? 0) === 0)
                                    ? "error.main"
                                    : "text.primary"
                                }
                              >
                                {c.criterionName || c.criterionNameAr || "Item"}
                                {c.quantifiable && c.quantity != null && ` (${c.quantity} pcs)`}
                                {(c.present === false ||
                                  (c.quantifiable && (c.quantity ?? 0) === 0)) &&
                                  " (Missing)"}
                              </MDTypography>
                            </li>
                          ))}
                        </MDBox>
                      ) : (
                        <MDTypography variant="body2" color="text.secondary">
                          {reservation.status === "CheckedOut"
                            ? "No criteria recorded."
                            : "Not yet checked out."}
                        </MDTypography>
                      )}
                    </MDBox>
                  </Grid>
                  {reservation.checkOut?.damagedItems?.length > 0 && (
                    <Grid item xs={12}>
                      <Divider sx={{ my: 2 }} />
                      <MDBox
                        sx={{
                          bgcolor: "rgba(237, 108, 2, 0.08)",
                          p: 2,
                          borderRadius: 2,
                          border: "1px solid",
                          borderColor: "warning.light",
                        }}
                      >
                        <MDTypography
                          variant="subtitle2"
                          fontWeight="bold"
                          mb={1}
                          color="warning.main"
                        >
                          Damaged Items (cost estimate in 2-4 days)
                        </MDTypography>
                        <MDBox component="ul" sx={{ m: 0, pl: 2 }}>
                          {reservation.checkOut.damagedItems.map((d, i) => (
                            <li key={i}>
                              <MDTypography variant="body2">
                                {d.name}
                                {d.description && ` - ${d.description}`}
                              </MDTypography>
                            </li>
                          ))}
                        </MDBox>
                      </MDBox>
                    </Grid>
                  )}
                  {(reservation.checkOut?.missingItems?.length > 0 ||
                    (reservation.checkOut?.insuranceDeduction || 0) > 0) && (
                    <Grid item xs={12}>
                      <Divider sx={{ my: 2 }} />
                      <MDBox
                        sx={{
                          bgcolor: "rgba(211, 47, 47, 0.08)",
                          p: 2,
                          borderRadius: 2,
                          border: "1px solid",
                          borderColor: "error.light",
                        }}
                      >
                        <MDTypography
                          variant="subtitle2"
                          fontWeight="bold"
                          mb={1}
                          color="error.main"
                        >
                          Insurance Deduction
                        </MDTypography>
                        <MDTypography variant="body2" mb={1}>
                          Total deducted:{" "}
                          {formatCurrency(reservation.checkOut?.insuranceDeduction || 0)}
                        </MDTypography>
                        <MDBox component="ul" sx={{ m: 0, pl: 2 }}>
                          {(reservation.checkOut?.missingItems || []).map((m, i) => (
                            <li key={i}>
                              <MDTypography variant="body2">
                                {m.name}
                                {m.quantity > 1 && ` (${m.quantity} pcs)`} -{" "}
                                {formatCurrency(m.clientPrice)}
                              </MDTypography>
                            </li>
                          ))}
                        </MDBox>
                      </MDBox>
                    </Grid>
                  )}
                </Grid>
              </Card>
            </Grid>

            {/* Housekeeping Report – before/after photos, duration, staff (Admin/Super Admin) */}
            <Grid item xs={12}>
              <Card
                sx={{ p: 3, borderRadius: 3, border: "1px solid", borderColor: "success.light" }}
              >
                <MDTypography
                  variant="h6"
                  fontWeight="bold"
                  mb={2}
                  display="flex"
                  alignItems="center"
                  gap={1}
                >
                  <Icon color="success">cleaning_services</Icon> Housekeeping Report
                </MDTypography>
                <Divider sx={{ mb: 2 }} />
                {!reservation.housekeeping || reservation.housekeeping.status === "Pending" ? (
                  <MDTypography variant="body2" color="text.secondary">
                    {reservation.status === "CheckedOut"
                      ? "Cleaning not started yet. Housekeeping will upload before/after photos and record time when they start and complete."
                      : "Housekeeping report will appear after guest checks out and housekeeping completes cleaning."}
                  </MDTypography>
                ) : (
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <MDBox
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        mb={1}
                      >
                        <MDTypography variant="body2" color="text.secondary">
                          Status
                        </MDTypography>
                        <Chip
                          label={reservation.housekeeping.status}
                          size="small"
                          color={
                            reservation.housekeeping.status === "Completed" ? "success" : "info"
                          }
                          sx={{ fontWeight: 600 }}
                        />
                      </MDBox>
                      {reservation.housekeeping.startedAt && (
                        <MDBox
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                          mb={1}
                        >
                          <MDTypography variant="body2" color="text.secondary">
                            Started
                          </MDTypography>
                          <MDTypography variant="body2" fontWeight="medium">
                            {formatDateTime(reservation.housekeeping.startedAt)}
                          </MDTypography>
                        </MDBox>
                      )}
                      {reservation.housekeeping.completedAt && (
                        <MDBox
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                          mb={1}
                        >
                          <MDTypography variant="body2" color="text.secondary">
                            Completed
                          </MDTypography>
                          <MDTypography variant="body2" fontWeight="medium">
                            {formatDateTime(reservation.housekeeping.completedAt)}
                          </MDTypography>
                        </MDBox>
                      )}
                      {reservation.housekeeping.cleaningDurationMinutes != null &&
                        reservation.housekeeping.cleaningDurationMinutes >= 0 && (
                          <MDBox
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            mb={1}
                          >
                            <MDTypography variant="body2" color="text.secondary">
                              Cleaning duration
                            </MDTypography>
                            <MDTypography variant="body2" fontWeight="bold" color="success.main">
                              {reservation.housekeeping.cleaningDurationMinutes < 60
                                ? `${reservation.housekeeping.cleaningDurationMinutes} min`
                                : `${Math.floor(
                                    reservation.housekeeping.cleaningDurationMinutes / 60
                                  )}h ${reservation.housekeeping.cleaningDurationMinutes % 60} min`}
                            </MDTypography>
                          </MDBox>
                        )}
                      {reservation.housekeeping.startedBy?.fullName && (
                        <MDBox
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                          mb={1}
                        >
                          <MDTypography variant="body2" color="text.secondary">
                            Started by
                          </MDTypography>
                          <MDTypography variant="body2" fontWeight="medium">
                            {reservation.housekeeping.startedBy.fullName}
                          </MDTypography>
                        </MDBox>
                      )}
                      {reservation.housekeeping.completedBy?.fullName && (
                        <MDBox
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                          mb={1}
                        >
                          <MDTypography variant="body2" color="text.secondary">
                            Completed by
                          </MDTypography>
                          <MDTypography variant="body2" fontWeight="medium">
                            {reservation.housekeeping.completedBy.fullName}
                          </MDTypography>
                        </MDBox>
                      )}
                      {reservation.housekeeping.notes && (
                        <MDBox mt={1.5}>
                          <MDTypography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                            mb={0.5}
                          >
                            Notes
                          </MDTypography>
                          <MDTypography variant="body2">
                            {reservation.housekeeping.notes}
                          </MDTypography>
                        </MDBox>
                      )}
                    </Grid>
                    <Grid item xs={12} md={6}>
                      {reservation.housekeeping.beforePhotos?.length > 0 && (
                        <MDBox mb={2}>
                          <MDTypography
                            variant="subtitle2"
                            fontWeight="bold"
                            mb={1}
                            color="text.secondary"
                          >
                            Before cleaning ({reservation.housekeeping.beforePhotos.length})
                          </MDTypography>
                          <Grid container spacing={1}>
                            {reservation.housekeeping.beforePhotos.slice(0, 6).map((url, i) => (
                              <Grid item xs={6} sm={4} key={i}>
                                <MDBox
                                  component="img"
                                  src={url}
                                  alt={`Before ${i + 1}`}
                                  sx={{
                                    width: "100%",
                                    height: 80,
                                    objectFit: "cover",
                                    borderRadius: 1,
                                    cursor: "pointer",
                                    border: "1px solid",
                                    borderColor: "divider",
                                  }}
                                  onClick={() => window.open(url, "_blank")}
                                />
                              </Grid>
                            ))}
                          </Grid>
                          {reservation.housekeeping.beforePhotos.length > 6 && (
                            <MDTypography variant="caption" color="text.secondary">
                              +{reservation.housekeeping.beforePhotos.length - 6} more
                            </MDTypography>
                          )}
                        </MDBox>
                      )}
                      {reservation.housekeeping.afterPhotos?.length > 0 && (
                        <MDBox>
                          <MDTypography
                            variant="subtitle2"
                            fontWeight="bold"
                            mb={1}
                            color="text.secondary"
                          >
                            After cleaning ({reservation.housekeeping.afterPhotos.length})
                          </MDTypography>
                          <Grid container spacing={1}>
                            {reservation.housekeeping.afterPhotos.slice(0, 6).map((url, i) => (
                              <Grid item xs={6} sm={4} key={i}>
                                <MDBox
                                  component="img"
                                  src={url}
                                  alt={`After ${i + 1}`}
                                  sx={{
                                    width: "100%",
                                    height: 80,
                                    objectFit: "cover",
                                    borderRadius: 1,
                                    cursor: "pointer",
                                    border: "1px solid",
                                    borderColor: "divider",
                                  }}
                                  onClick={() => window.open(url, "_blank")}
                                />
                              </Grid>
                            ))}
                          </Grid>
                          {reservation.housekeeping.afterPhotos.length > 6 && (
                            <MDTypography variant="caption" color="text.secondary">
                              +{reservation.housekeeping.afterPhotos.length - 6} more
                            </MDTypography>
                          )}
                        </MDBox>
                      )}
                    </Grid>
                  </Grid>
                )}
              </Card>
            </Grid>

            <Grid item xs={12}>
              <Card sx={{ p: 3, borderRadius: 3 }}>
                <MDTypography
                  variant="h6"
                  fontWeight="bold"
                  mb={2}
                  display="flex"
                  alignItems="center"
                  gap={1}
                >
                  <Icon color="secondary">contact_mail</Icon> Booking Contact
                </MDTypography>
                <Divider sx={{ mb: 2 }} />
                {reservation.client?.fullName && (
                  <MDBox mb={1}>
                    <MDTypography variant="caption" color="text.secondary">
                      Name
                    </MDTypography>
                    <MDTypography variant="body2" fontWeight="medium">
                      {reservation.client.fullName}
                    </MDTypography>
                  </MDBox>
                )}
                <MDBox mb={reservation.client?.phone ? 1 : 0}>
                  <MDTypography variant="caption" color="text.secondary">
                    Email
                  </MDTypography>
                  <MDTypography variant="body2" fontWeight="medium">
                    {reservation.client?.email || "N/A"}
                  </MDTypography>
                </MDBox>
                {reservation.client?.phone && (
                  <MDBox>
                    <MDTypography variant="caption" color="text.secondary">
                      Phone
                    </MDTypography>
                    <MDTypography variant="body2">{reservation.client.phone}</MDTypography>
                  </MDBox>
                )}
              </Card>
            </Grid>
          </Grid>
        )}
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default CheckInOutReservationDetails;
